'use client';

import { FiltersPanel } from '@/app/orders/components/FiltersPanel';
import TrackingOrders from '@/app/orders/components/TrackingOrders';

import { GetOrdersParams } from '@/core/use-cases/TrackingOrderListUseCase';

import { DeliveryPlanType } from '@/core/entities/delivery-plan-type';
import { OrderSearchResultVM } from '@/core/entities/order-search-result';
import { Store } from '@/core/entities/store';

import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchTrackingOrderAction, FetchTrackingResult } from '@/core/actions/traking-order-list';
import { useLoading } from '@/core/hooks/useLoading';
import { OrdersState, useOrdersState } from '@/core/hooks/useOrdersState';
import { useStableErrorHandler } from '@/core/hooks/useStableErrorHandler';
import {
  trackClickSearch,
  trackDocumentLookup,
  trackSearchClick
} from '@/core/utils/analytics.util';
import { SESSION_KEYS } from '@/core/utils/constants';
import { getDocumentType } from '@/core/utils/order.util';

interface OrdersSearchBarProps {
  stores: Store[];
  deliveryPlanTypes: DeliveryPlanType[];
  initialOrdersData?: OrderSearchResultVM;
  initialOrdersState?: OrdersState;
}

/**
 * Componente principal para la página de búsqueda de órdenes. Contiene el panel de filtros y la tabla de seguimiento de órdenes.
 *
 * @param {OrdersSearchBarProps} props - Las propiedades del componente.
 *
 * @returns {JSX.Element} El componente renderizado.
 */
export default function OrdersSearchBar({
  stores,
  deliveryPlanTypes,
  initialOrdersData,
  initialOrdersState
}: OrdersSearchBarProps) {
  const { loading, error, setLoadingState, getTableLoadingConfig } = useLoading();
  const {
    state: ordersState,
    updateFilters,
    updatePagination,
    resetFilters,
    resetToIdOnly,
    toggleAdvanced,
    triggerSearch,
    resetSearchFlag
  } = useOrdersState(initialOrdersState);

  const { handleError, isBlocked } = useStableErrorHandler();

  const [ordersData, setOrdersData] = useState<OrderSearchResultVM | undefined>(initialOrdersData);

  const hasValidFilters = useCallback((state: typeof ordersState): boolean => {
    return !!(
      state.docId ||
      state.purchaseOrder ||
      state.creditAccount ||
      (state.orderCreationDateSince && state.orderCreationDateUntil) ||
      (state.deliveryPromiseDateSince && state.deliveryPromiseDateUntil) ||
      state.selectedStores.length > 0
    );
  }, []);

  useEffect(() => {
    sessionStorage.removeItem(SESSION_KEYS.SEARCHBAR_BACKBUTTON);
  }, []);

  const buildAPIParams = useCallback((state: typeof ordersState): GetOrdersParams => {
    const mappedDocType = getDocumentType(state.docType);

    return {
      ...(mappedDocType && { docType: mappedDocType }),
      ...(state.docId && { docId: state.docId }),
      ...(state.purchaseOrder && { purchaseOrder: state.purchaseOrder }),
      ...(state.creditAccount && { creditAccount: state.creditAccount }),
      ...(state.orderCreationDateSince && { orderCreationDateSince: state.orderCreationDateSince }),
      ...(state.orderCreationDateUntil && { orderCreationDateUntil: state.orderCreationDateUntil }),
      ...(state.deliveryPromiseDateSince && {
        deliveryPromiseDateSince: state.deliveryPromiseDateSince
      }),
      ...(state.deliveryPromiseDateUntil && {
        deliveryPromiseDateUntil: state.deliveryPromiseDateUntil
      }),
      ...(state.deliveryPlanType &&
        state.deliveryPlanType.length > 0 && { deliveryPlanType: state.deliveryPlanType }),
      ...(state.selectedStores.length > 0 && { store: state.selectedStores }),
      page: state.currentPage,
      limit: state.pageSize
    };
  }, []);

  const executeSearch = useCallback(async () => {
    if (!hasValidFilters(ordersState)) return;
    setLoadingState({ loading: true, error: null });
    const apiParams = buildAPIParams(ordersState);

    try {
      const determineSearchType = (): 'factura' | 'pedido' | 'pick-slip' => {
        const dt = ordersState.docType;
        if (dt === 'pedido') return 'pedido';
        if (dt === 'pickslip' || dt === 'pick-slip') return 'pick-slip';
        return 'factura';
      };
      const determineSearchValue = (): string => {
        if (ordersState.docId) return ordersState.docId;
        if (ordersState.purchaseOrder) return ordersState.purchaseOrder;
        if (ordersState.creditAccount) return ordersState.creditAccount;
        return '';
      };

      const result: FetchTrackingResult = await fetchTrackingOrderAction(apiParams);

      if (result.success && result.data) {
        const toStore = (v: unknown) => {
          if (!v) return undefined;
          if (typeof v === 'string') {
            return { id: null, name: v, organizationId: null, companyId: null };
          }
          if (typeof v === 'object' && v !== null) {
            return v as {
              id: string | null;
              name: string | null;
              organizationId: string | null;
              companyId: string | null;
            };
          }
          return undefined;
        };

        const normalized: OrderSearchResultVM = {
          ...result.data,
          data: (result.data.data || []).map((it) => {
            const storeObj = toStore(it.store as unknown);
            const originObj = toStore(it.originStore as unknown);
            const deliveryObj = toStore(it.deliveryStore as unknown) ?? storeObj ?? originObj;
            return {
              ...it,
              store: storeObj,
              originStore: originObj,
              deliveryStore: deliveryObj
            };
          })
        };

        setOrdersData(normalized);

        trackSearchClick({
          searchType: determineSearchType(),
          searchValue: determineSearchValue(),
          resultCount: normalized.data.length
        });

        if (ordersState.isManualSearch) {
          const st = determineSearchType();
          const mappedType = st === 'factura' ? 'INVOICE' : st === 'pedido' ? 'WEB_ORDER' : null;
          if (mappedType) {
            trackClickSearch({
              search_type: mappedType,
              search_input_length: determineSearchValue().length,
              has_results: normalized.data.length > 0,
              result_count: normalized.data.length
            });
          }
        }

        if (ordersState.docId) {
          const st = determineSearchType();
          if (st === 'factura' || st === 'pedido') {
            void trackDocumentLookup({
              docId: ordersState.docId,
              lookupResult: normalized.data.length > 0 ? 'FOUND' : 'NOT_FOUND',
              docType: st
            });
          }
        }

        setLoadingState({
          loading: false,
          error: normalized.data.length === 0 ? true : null
        });
      } else {
        trackSearchClick({
          searchType: determineSearchType(),
          searchValue: determineSearchValue(),
          resultCount: 0
        });
        if (ordersState.isManualSearch) {
          const st = determineSearchType();
          const mappedType = st === 'factura' ? 'INVOICE' : st === 'pedido' ? 'WEB_ORDER' : null;
          if (mappedType) {
            trackClickSearch({
              search_type: mappedType,
              search_input_length: determineSearchValue().length,
              has_results: false,
              result_count: 0
            });
          }
        }
        if (ordersState.docId) {
          const st = determineSearchType();
          if (st === 'factura' || st === 'pedido') {
            void trackDocumentLookup({
              docId: ordersState.docId,
              lookupResult: 'NOT_FOUND',
              docType: st
            });
          }
        }
        setOrdersData(undefined);
        setLoadingState({ loading: false, error: true });
        if (ordersState.isManualSearch) {
          const handleGoBack = () => {
            setLoadingState({ loading: false, error: false });
          };

          handleError(
            new Error(
              'No pudimos obtener la información en este momento. Intenta recargando la página o vuelve a intentarlo más tarde.'
            ),
            'fetchTrackingOrderAction',
            executeSearch,
            handleGoBack
          );
        }
      }
    } catch (error) {
      setOrdersData(undefined);
      setLoadingState({ loading: false, error: error as Error });

      const determineSearchType = (): 'factura' | 'pedido' | 'pick-slip' => {
        const dt = ordersState.docType;
        if (dt === 'pedido') return 'pedido';
        if (dt === 'pickslip' || dt === 'pick-slip') return 'pick-slip';
        return 'factura';
      };
      const determineSearchValue = (): string => {
        if (ordersState.docId) return ordersState.docId;
        if (ordersState.purchaseOrder) return ordersState.purchaseOrder;
        if (ordersState.creditAccount) return ordersState.creditAccount;
        return '';
      };
      trackSearchClick({
        searchType: determineSearchType(),
        searchValue: determineSearchValue(),
        resultCount: 0
      });

      if (ordersState.isManualSearch) {
        const st = determineSearchType();
        const mappedType = st === 'factura' ? 'INVOICE' : st === 'pedido' ? 'WEB_ORDER' : null;
        if (mappedType) {
          trackClickSearch({
            search_type: mappedType,
            search_input_length: determineSearchValue().length,
            has_results: false,
            result_count: 0
          });
        }
      }

      if (ordersState.docId) {
        const st = determineSearchType();
        if (st === 'factura' || st === 'pedido') {
          void trackDocumentLookup({
            docId: ordersState.docId,
            lookupResult: 'ERROR',
            docType: st
          });
        }
      }

      if (ordersState.isManualSearch) {
        const handleGoBack = () => {
          setLoadingState({ loading: false, error: false });
        };

        handleError(error, 'fetchTrackingOrderAction', executeSearch, handleGoBack);
      }
    }
  }, [ordersState, hasValidFilters, buildAPIParams, setLoadingState, handleError]);

  const hasHydratedRef = useRef(false);

  useEffect(() => {
    if (!hasHydratedRef.current) {
      hasHydratedRef.current = true;
      if (initialOrdersData) {
        resetSearchFlag();
        return; // Evita fetch si el servidor ya lo hizo
      }
    }

    if (ordersState.shouldSearch && hasValidFilters(ordersState)) {
      if (isBlocked) {
        resetSearchFlag();
        return;
      }

      executeSearch().finally(() => {
        resetSearchFlag();
      });
    } else if (ordersState.shouldSearch && !hasValidFilters(ordersState)) {
      setOrdersData(undefined);
      setLoadingState({ loading: false, error: null });
      resetSearchFlag();
    }
  }, [
    ordersState.shouldSearch,
    hasValidFilters,
    setLoadingState,
    resetSearchFlag,
    isBlocked,
    executeSearch,
    ordersState,
    initialOrdersData
  ]);

  return (
    <>
      <FiltersPanel
        stores={stores}
        deliveryPlanTypes={deliveryPlanTypes}
        ordersState={ordersState}
        updateFilters={updateFilters}
        resetFilters={resetFilters}
        resetToIdOnly={resetToIdOnly}
        toggleAdvanced={toggleAdvanced}
        triggerSearch={triggerSearch}
        setLoadingState={setLoadingState}
        loading={loading}
      />
      <TrackingOrders
        ordersData={ordersData}
        ordersState={ordersState}
        updatePagination={updatePagination}
        loading={loading}
        error={error}
        setLoadingState={setLoadingState}
        getTableLoadingConfig={getTableLoadingConfig}
      />
    </>
  );
}
